import React from 'react';
import { Phone, Mail, Clock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

/**
 * ContactSection Component
 * 
 * A comprehensive contact section that combines contact information display
 * and a contact form. The component is split into two main parts:
 * 1. Contact Information Table - displays key contact methods and hours
 * 2. Contact Form - allows users to submit inquiries and request estimates
 * 
 * The component features:
 * - Responsive layout that adapts to different screen sizes
 * - Accessible form inputs with proper labeling
 * - Theme-aware styling with appropriate contrast
 * - Interactive elements with hover states
 * - Clear visual hierarchy of information
 */
const ContactSection = () => {
  const { theme } = useTheme();

  // Contact information data structure
  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '(863) 682-7663',
      link: 'tel:8636827663'
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'contact@mobilehomerepairfl.com',
      link: 'mailto:contact@mobilehomerepairfl.com'
    },
    {
      icon: Clock,
      label: 'Hours',
      value: ['24/7 Emergency Service', 'Regular Hours: Mon-Sat: 8AM-6PM'],
      link: null
    }
  ];



  return (
    <section className={`py-16 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
    }`} id="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Contact Us
          </h2>
          <p className={`text-xl ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Reach out to our team of experts for all your mobile home repair needs.
          </p>
        </div>

        {/* Contact Content Grid */}
        <div className="max-w-3xl mx-auto">
          {/* Contact Information Table */}
          <div className={`rounded-lg overflow-hidden border ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <table className="w-full">
              <thead>
                <tr className={
                  theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                }>
                  <th className={`px-6 py-4 text-left text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Contact Information
                  </th>
                </tr>
              </thead>
              <tbody className={
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              }>
                {contactInfo.map((info, index) => (
                  <tr key={index} className={`border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {/* Icon Container */}
                        <div className={`p-2 rounded-lg ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <info.icon className={
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-900'
                          } />
                        </div>
                        {/* Contact Details */}
                        <div>
                          <p className={`font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {info.label}
                          </p>
                          {Array.isArray(info.value) ? (
                            info.value.map((v, i) => (
                              <p key={i} className={
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }>
                                {v}
                              </p>
                            ))
                          ) : (
                            info.link ? (
                              <a 
                                href={info.link}
                                className={`${
                                  theme === 'dark' ? 'text-blue-400' : 'text-blue-900'
                                } hover:underline`}
                              >
                                {info.value}
                              </a>
                            ) : (
                              <p className={
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }>
                                {info.value}
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


        </div>
      </div>
    </section>
  );
};

export default ContactSection;